/**
 * ConnectPage — the connect-first front door (PDR 011 D6/D7/D18).
 *
 * Revives, without the demo branch, the connect/welcome page PDR 010 retired.
 * It is the screen a signed-in-but-unconnected identity sees IN PLACE OF a
 * workspace: under the D18 hard connect-gate no workspace/container/slot exists
 * until a Google or Microsoft account is linked, so "no workspace yet" IS the
 * connect state. The App gate chain renders this only at the `!hasUsableWorkspace`
 * leaf for a never-trialed, non-invitee identity (consent already satisfied
 * upstream — identity → T&C → connect).
 *
 * PURE PRESENTATIONAL: the two side-effecting dependencies are injected as props
 * so this stays in `features/auth` without reaching into `features/oauth`
 * (the connect flow) or `app/providers` (feedback) — App wires both:
 *   - `onConnect(provider)` drives the ONE connect flow (`connectProvider`).
 *     The backend owns scope resolution, so this screen never names individual
 *     scopes (no "calendar"/"Outlook" copy) and offers no demo option (D6).
 *   - `onDeclinedFeedback(tag)` opens the EXISTING feedback modal as a
 *     `feature_request` tagged `[declined_connect]` — a decline is a signal, not
 *     a dead end (PDR 011 §5); no new feedback plumbing.
 */

import { useState } from 'react'
import { env } from '@/lib/env'
import { GoogleIcon, MicrosoftIcon } from './ProviderIcons'

export type ConnectProvider = 'google' | 'microsoft'

export interface ConnectPageProps {
  /** Start the data-grant connect for a provider. Resolves on success (the page
   *  normally navigates away); rejects if the authorize-URL fetch failed. */
  onConnect: (provider: ConnectProvider) => Promise<void>
  /** Open the existing feedback modal pre-tagged for the declined-connect signal. */
  onDeclinedFeedback: (tag: string) => void
}

export function ConnectPage({ onConnect, onDeclinedFeedback }: ConnectPageProps) {
  // Which provider's connect is mid-redirect (disables both buttons), and any
  // failure to fetch the authorize URL (the success path navigates away).
  const [connecting, setConnecting] = useState<ConnectProvider | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleConnect = async (provider: ConnectProvider) => {
    setError(null)
    setConnecting(provider)
    try {
      await onConnect(provider)
    } catch {
      setConnecting(null)
      setError('Could not start the connection. Please try again.')
    }
  }

  return (
    <div
      // `relative z-10` lifts this full-screen gate above the fixed, OPAQUE
      // WoodCanvas (`.wood-canvas` is position:fixed z-index:0 on the nordic/
      // atelier themes, #3871). Without the lift the canvas paints over this
      // static, in-flow screen and the user sees a blank wood background instead
      // of the connect screen — the same z-index lift the other post-auth
      // screens already carry.
      className="connect-page relative z-10 min-h-[100dvh] flex items-center justify-center p-5 overflow-x-hidden"
      style={{ background: '#f8f7f5' }}
    >
      <div
        className="bg-white rounded-2xl p-10 w-full border"
        style={{ maxWidth: '420px', borderColor: '#e8e6e2' }}
      >
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div
              className="xavior-logo-mark w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ background: '#0ea5e9' }}
            >
              <span className="text-white text-sm font-bold">X</span>
            </div>
            <span
              className="text-lg font-medium text-gray-900"
              style={{ letterSpacing: '0.12em', textTransform: 'uppercase' }}
            >
              XAVIOR
            </span>
          </div>
        </div>

        {/* Heading — scope-neutral (D6: no calendar/email/Outlook copy) */}
        <div className="text-center mb-6">
          <h2 className="text-2xl font-normal text-gray-900 mb-2">Connect your account</h2>
          <p className="text-gray-400 text-sm">Link Google or Microsoft to set up your workspace</p>
        </div>

        {/* Provider buttons — both offered, magic-link and SSO alike (D6/D7) */}
        <button
          type="button"
          onClick={() => handleConnect('google')}
          disabled={connecting !== null}
          className="w-full py-3 px-4 bg-white hover:bg-gray-50 text-gray-700 font-medium rounded-xl text-sm border flex items-center justify-center gap-3 mb-3 disabled:opacity-50 disabled:cursor-default"
          style={{ borderColor: '#e8e6e2', transition: 'background 0.15s ease' }}
        >
          <GoogleIcon />
          {connecting === 'google' ? 'Connecting…' : 'Connect Google'}
        </button>

        <button
          type="button"
          onClick={() => handleConnect('microsoft')}
          disabled={connecting !== null}
          className="w-full py-3 px-4 bg-white hover:bg-gray-50 text-gray-700 font-medium rounded-xl text-sm border flex items-center justify-center gap-3 mb-4 disabled:opacity-50 disabled:cursor-default"
          style={{ borderColor: '#e8e6e2', transition: 'background 0.15s ease' }}
        >
          <MicrosoftIcon />
          {connecting === 'microsoft' ? 'Connecting…' : 'Connect Microsoft'}
        </button>

        <div className="h-5 mb-2">
          {error && <p className="text-center text-sm text-[var(--color-danger)]">{error}</p>}
        </div>

        {/* Declined-to-connect → demand signal (reuse the feedback path) */}
        <div className="flex flex-col items-center gap-2 mt-2">
          <button
            type="button"
            onClick={() => onDeclinedFeedback('[declined_connect][imap_caldav]')}
            className="text-xs text-gray-400 hover:text-gray-600 underline-offset-2 hover:underline"
          >
            IMAP &amp; CalDAV are coming — want them? Let us know
          </button>
        </div>

        {/* Consent already accepted upstream — informational link only, no gate */}
        <p className="text-center text-xs text-gray-400 mt-6">
          By connecting you agree to our{' '}
          <a href={env.termsUrl} target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600">
            Terms
          </a>{' '}
          and{' '}
          <a href={env.privacyUrl} target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600">
            Privacy Policy
          </a>
          .
        </p>
      </div>
    </div>
  )
}
